import { Loader, Text } from "@mantine/core";
import { useContext } from "react";
import { useParams } from "react-router-dom";
import {
  usePatchSession,
  useCancelSession,
  useDeleteSessionLink,
  useCreateSessionLink,
  useGetSession,
} from "../../../api/session";
import { useGetSponsors } from "../../../api/sponsors";
import {
  getCurrentWorkspace,
  isCurrentEventAdmin,
} from "../../../services/userConfig";
import { AuthContext } from "../../../services/context";
import {
  useGetPeopleTagsBySubtype,
  useGetSessionTagsByEventId,
  useGetSessionTagsBySessionId,
} from "../../../api/tag";
import { useGetAllSpeakers } from "../../../api/speakers";
import { useGetEvent, useGetEventLocations } from "../../../api/event";
import SessionForm from "../Form";

export default function EditSession() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);

  const { data: event } = useGetEvent(currentWorkspace.eventId);
  const enableActions = isCurrentEventAdmin(user);
  const { data: responseData, isPending, isError, error } = useGetSession(id);

  const { data: speakers } = useGetAllSpeakers({
    eventId: currentWorkspace.eventId,
    limit: 1000,
  });

  const speakerOptions = speakers?.data?.map((speaker) => ({
    label: `${speaker.firstName} ${speaker.lastName}, ${speaker.occupation}`,
    value: speaker.id.toString(),
  }));

  const { data: locations } = useGetEventLocations(currentWorkspace.eventId);
  const locationsOptions = locations?.map((location) => ({
    label: location.name,
    value: location.id.toString(),
  }));

  const { data: sponsors } = useGetSponsors({
    workspaceId: currentWorkspace.id,
    eventId: currentWorkspace.eventId,
  });

  const { data: tagOptions } = useGetSessionTagsByEventId(
    currentWorkspace.eventId
  );
  const { data: sessionTags } = useGetSessionTagsBySessionId(id);

  const { data: categoryTags } = useGetPeopleTagsBySubtype({
    subtype: "categoryOfUser",
    eventId: currentWorkspace.eventId,
  });

  const { mutate: patchSession, isPending: isLoading } = usePatchSession(id);
  const { mutate: cancel } = useCancelSession(
    id,
    responseData?.canceledAt !== null
  );
  const { mutate: deleteLink } = useDeleteSessionLink(id);
  const { mutate: createLink } = useCreateSessionLink(id);

  if (isPending) {
    return <Loader mt={10} />;
  }

  if (isError) {
    return <Text c="red">{error.message}</Text>;
  }

  if (responseData && speakerOptions && sponsors && tagOptions && sessionTags) {
    const sponsorOptions = sponsors?.data.map((s) => ({
      label: s.name,
      value: s.id.toString(),
    }));

    const onSubmit = (formData, { onSuccess }) => {
      patchSession(formData, {
        onSuccess,
      });
    };

    return (
      <SessionForm
        mode="edit"
        data={responseData}
        enableActions={enableActions}
        speakerOptions={speakerOptions}
        sponsorOptions={sponsorOptions}
        tagOptions={tagOptions}
        categoryTagOptions={categoryTags}
        sessionTags={sessionTags}
        locationsOptions={locationsOptions}
        event={event}
        onSubmit={onSubmit}
        onCancel={cancel}
        onDeleteLink={deleteLink}
        onCreateLink={createLink}
        isLoading={isLoading}
      />
    );
  }
}
